import { showMessage } from '../../utils/showMessage/index.js';
import { parentElements } from '../../utils/parentElements/index.js';
import { loadGrecaptcha } from '../../async/loadGrecaptcha.js';
import { getRecaptchaV3PublicKey, reloadCaptcha } from '../../captcha.js';
import Viewport from '../../common/viewport.js';

const FORM_CLASS = 'prk-modern-form';
const FORM_INITIALIZED_CLASS = `${FORM_CLASS}--initialized`;
const FORM_SENDING_CLASS = `${FORM_CLASS}--sending`;
const FORM_SUBMITTED_SUCCESSFULLY_CLASS = `${FORM_CLASS}--submitted`;
const FORM_LARGE_FILE_UPLOAD_CLASS = `${FORM_CLASS}--large-file-upload`;

const FORM_UPLOAD_PROGRESS_BAR_CLASS = `${FORM_CLASS}__upload-progress-bar`;
const FORM_LARGE_FILE_SIZE_THRESHOLD = 200 * 1024; // 200KB

const FORM_FIELD_CLASS = `${FORM_CLASS}__field`;
const FORM_INPUT_CLASS = `${FORM_CLASS}__input`;
const FORM_INPUT_INITIALIZED_CLASS = `${FORM_INPUT_CLASS}--initialized`;
const FORM_INPUT_ERROR_CLASS = `${FORM_INPUT_CLASS}-error`;

const CHECKBOX_CLASS = `${FORM_CLASS}__checkbox`;
const CHECKBOX_INPUT_MOCK_CLASS = `${CHECKBOX_CLASS}-input-mock`;
const CHECKBOX_CAPTION_CLASS = `${CHECKBOX_CLASS}-caption`;

const SELECT_CLASS = `${FORM_CLASS}__select`;
const SELECT_NOT_SELECTED_CLASS = `${SELECT_CLASS}--not-selected`;

const FILE_CLASS = `${FORM_CLASS}__file`;
const FILE_TITLE_CLASS = `${FILE_CLASS}-title`;
const FILE_FILENAME_CLASS = `${FILE_CLASS}-filename`;

class PrkModernFormInput {
    /** @var {HTMLInputElement} inputElement */
    inputElement;

    /** @var {HTMLElement} fieldElement */
    fieldElement;

    /** @var {HTMLElement} errorsElement */
    errorsElement;

    /** @var {string[]} _validationErrors */
    _validationErrors = [];

    /**
     * @param {HTMLInputElement|HTMLSelectElement} inputElement
     */
    constructor(inputElement) {
        this.inputElement = inputElement;
        this.fieldElement = parentElements(this.inputElement, el => el.classList.contains(FORM_FIELD_CLASS))[0] ?? null;
        this.errorsElement = this.fieldElement?.querySelector(`.${FORM_INPUT_ERROR_CLASS}`) ?? null;

        this.inputElement.classList.add(FORM_INPUT_INITIALIZED_CLASS);
    }

    /**
     * @return {string}
     */
    get name() {
        return this.inputElement.name;
    }

    /**
     * @return {string}
     */
    get value() {
        return this.inputElement.value;
    }

    /**
     * @param {string} v
     */
    set value(v) {
        this.inputElement.value;
    }

    /**
     * @return {string[]}
     */
    get validationErrors() {
        return this._validationErrors;
    }

    /**
     * @param {string[]} errors
     */
    set validationErrors(errors) {
        this._validationErrors = errors;

        if (!this.errorsElement)
            return;

        this.errorsElement.innerHTML = this._validationErrors?.length
            ? `- ${this._validationErrors.join('<br>- ')}`
            : '';
    }

    /**
     * @return {boolean}
     */
    get isDisabled() {
        return this.inputElement.hasAttribute('disabled')
            && 'false' !== this.inputElement.getAttribute('disabled');
    }

    /**
     * @param {boolean} v
     */
    set isDisabled(v) {
        if ('boolean' !== typeof v)
            return;

        if (v)
            this.inputElement.setAttribute('disabled', 'disabled');
        else
            this.inputElement.removeAttribute('disabled');
    }
}

class PrkModernFormCheckbox extends PrkModernFormInput {
    /** @var {HTMLElement} checkboxElement */
    checkboxElement;

    /** @var {HTMLElement} checkboxInputMockElement */
    checkboxInputMockElement;

    /** @var {HTMLElement} checkboxCaptionElement */
    checkboxCaptionElement;

    /**
     * @param {HTMLInputElement} checkboxInputElement
     */
    constructor(checkboxInputElement) {
        super(checkboxInputElement);
        this.checkboxElement = parentElements(this.inputElement, el => el.classList.contains(CHECKBOX_CLASS))[0] ?? null;
        this.checkboxInputMockElement = this.checkboxElement?.querySelector(`.${CHECKBOX_INPUT_MOCK_CLASS}`) ?? null;
        this.checkboxCaptionElement = this.checkboxElement?.querySelector(`.${CHECKBOX_CAPTION_CLASS}`) ?? null;

        this.eventListeners = {
            toggleChecked: this.toggleChecked.bind(this),
        };

        this.init();
    }

    init() {
        // this.checkboxElement.classList.add(CHECKBOX_INITIALIZED_CLASS);
        this.checkboxElement.addEventListener('click', this.eventListeners.toggleChecked);
    }

    /**
     * @param {PointerEvent|TouchEvent|MouseEvent} event
     */
    toggleChecked(event) {
        if (this.isDisabled)
            return;

        // Do not toggle on internal link clicks
        if (event?.target?.tagName === 'A') {
            return;
        }

        this.value = !this.value;
    }

    /**
     * @return {boolean}
     */
    get value() {
        return !!this.inputElement.checked;
    }

    /**
     * @param {boolean} v
     */
    set value(v) {
        if ('boolean' === typeof v)
            this.inputElement.checked = v;
    }
}

class PrkModernFormSelect extends PrkModernFormInput {
    /** @var {HTMLElement} selectElement */
    selectElement;

    /**
     * @param {HTMLElement} selectElement
     */
    constructor(selectInputElement) {
        super(selectInputElement);
        this.selectElement = parentElements(this.inputElement, el => el.classList.contains(SELECT_CLASS))[0] ?? null;

        this.eventListeners = {
            onSelect: this.onSelect.bind(this),
        };

        this.init();
    }

    init() {
        // this.selectElement.classList.add(SELECT_INITIALIZED_CLASS);
        this.inputElement.addEventListener('change', this.eventListeners.onSelect);
    }

    /**
     * @param {Event} event
     */
    onSelect(event) {
        if (this.inputElement.value) {
            this.selectElement.classList.remove(SELECT_NOT_SELECTED_CLASS);
            const selectedOption = this.selectElement.querySelector(`option[value="${this.inputElement.value}"]`);
            this.inputElement.setAttribute('title', selectedOption?.innerText);
        } else {
            this.selectElement.classList.add(SELECT_NOT_SELECTED_CLASS);
            this.inputElement.removeAttribute('title');
        }
    }
}

class PrkModernFormFile extends PrkModernFormInput {
    /** @var {HTMLElement} fileElement */
    fileElement;

    /** @var {HTMLElement} fileTitleElement */
    fileTitleElement;

    /**
     * @param {HTMLElement} fileElement
     */
    constructor(fileInputElement) {
        super(fileInputElement);
        this.fileElement = parentElements(this.inputElement, el => el.classList.contains(FILE_CLASS))[0] ?? null;
        this.fileTitleElement = this.fileElement?.querySelector(`.${FILE_TITLE_CLASS}`);

        this.eventListeners = {
            onFileSelect: this.onFileSelect.bind(this),
        };

        this.init();
    }

    init() {
        // this.fileElement.classList.add(FILE_INITIALIZED_CLASS);
        this.inputElement.addEventListener('input', this.eventListeners.onFileSelect);
    }

    /**
     * @param {InputEvent} event
     */
    onFileSelect(event) {
        const file = this.inputElement.files?.[0];
        const { fileSelectedTitle = 'Файл', fileEmptyTitle = 'Выберите файл' } = this.inputElement.dataset ?? {};

        if (fileSelectedTitle && fileEmptyTitle) {
            this.fileTitleElement.innerHTML = file
                ? `${fileSelectedTitle}: <span class="${FILE_FILENAME_CLASS}">${file.name}</span>`
                : fileEmptyTitle;
        }

        this.inputElement.blur();
    }

    /**
     * @return {File|null}
     */
    get value() {
        return this.inputElement.files?.[0] ?? null;
    }

    /**
     * Does nothing
     * @param {any} v
     */
    set value(v) {
        // Do nothing
    }
}

class PrkModernForm {
    /** @var {HTMLFormElement} formElement */
    formElement;

    /** @var {PrkModernFormInput[]} inputs */
    inputs = [];

    /** @var {XMLHttpRequest|null} sendFormRequest */
    sendFormRequest = null;

    /** @var {HTMLProgressElement} progressElement */
    uploadProgress;

    /**
     * !IMPORTANT only use via getter/setter
     * @var {boolean} _isSending
     */
    _isSending;

    /**
     * !IMPORTANT only use via getter/setter
     * @var {boolean} _isUploadingLargeFile
     */
    _isUploadingLargeFile;

    /**
     * !IMPORTANT only use via getter/setter
     * @var {boolean} _isSubmittedSuccessfully
     */
    _isSubmittedSuccessfully;

    /**
     * @param {HTMLFormElement} formElement
     */
    constructor(formElement) {
        this.formElement = formElement;
        this.uploadProgress = this.formElement.querySelector(`.${FORM_UPLOAD_PROGRESS_BAR_CLASS}`);
        this.eventListeners = {
            submit: this.submit.bind(this),
            onUploadProgress: this.onUploadProgress.bind(this),
        };

        this.init();
    }

    init() {
        this.formElement.classList.add(FORM_INITIALIZED_CLASS);
        this.formElement.addEventListener('submit', this.eventListeners.submit);

        this.initInputs();
    }

    initInputs() {
        const formInputs = [...this.formElement.querySelectorAll(
            `input[name]:not(.${FORM_INPUT_INITIALIZED_CLASS}), select[name]:not(.${FORM_INPUT_INITIALIZED_CLASS}), textarea[name]:not(.${FORM_INPUT_INITIALIZED_CLASS})`
        )];

        for (const inputElement of formInputs) {
            if (inputElement.tagName === 'SELECT') {
                this.inputs.push(new PrkModernFormSelect(inputElement));
                continue;
            }

            switch (inputElement.type) {
                case 'checkbox':
                    this.inputs.push(new PrkModernFormCheckbox(inputElement));
                    break;
                
                case 'file':
                    this.inputs.push(new PrkModernFormFile(inputElement));
                    break;
                
                default:
                    this.inputs.push(new PrkModernFormInput(inputElement));
                    break;
            }
        }
    }

    /**
     * @param {SubmitEvent} event 
     */
    async submit(event) {
        event.preventDefault();

        if (this.isSending)
            return;

        this.isSending = true;

        // Reset validation errors
        for (const input of this.inputs) {
            input.validationErrors = [];
        }

        try {
            const data = this.getFormData();
            
            // Add recaptcha to requset
            const { tokenV3, actionV3 } = await this.getRecaptchaAttributes(this.formElement.id);
            data.append('token_v3', tokenV3);
            data.append('action_v3', actionV3);

            const responseData = await this.send(data);
            this.isSubmittedSuccessfully = true;
        } catch ({
            message = 'Неизвестная ошибка. Попробуйте, пожалуйста, позже',
            error = undefined,
            validation_errors = undefined
        }) {
            if (validation_errors) {
                for (const input of this.inputs) {
                    input.validationErrors = validation_errors[input.name] ?? [];
                }
            } else {
                showMessage(
                    error?.message ?? message,
                    'Ошибка',
                    'our-brand-page-error-dialog-button'
                );
            }
        } finally {
            this.isSending = false;
            // reloadCaptcha('google');
        }
    }

    /**
     * @param {FormData} data
     * @return {Promise<{[key: string]: any},{error: { message: string}, validation_errors?: {[key: string]: string[]}}}
     */
    send(data) {
        return new Promise((resolve, reject) => {
            let filesSize = 0;
            for (const input of this.inputs) {
                if (input instanceof PrkModernFormFile) {
                    filesSize += data.get(input.name)?.size ?? 0;
                }
            }

            this.uploadProgress.value = 0;
            this.uploadProgress.max = filesSize;

            this.sendFormRequest = new XMLHttpRequest();
            this.sendFormRequest.upload.onprogress = this.eventListeners.onUploadProgress;
            this.sendFormRequest.onload = (event) => this.handleResponse(event, resolve, reject);
            this.sendFormRequest.onerror = (event) => this.handleUploadError(event, reject);

            this.sendFormRequest.open(this.formElement.method, this.formElement.action, true);
            this.sendFormRequest.send(data);
        });
    }

    /**
     * @return {FormData}
     */
    getFormData() {
        const data = new FormData();

        for (const input of this.inputs) {
            const value = input.value;

            if (value !== null)
                data.append(input.name, value);
        }

        return data;
    }

    /**
     * Retrives reCaptcha tokens
     * @param {string} action
     * @return {Promise<{tokenV3: string, actionV3: string}>}
     */
    getRecaptchaAttributes(action) {
        return new Promise((resolve, reject) => {
            const errorMessage = 'Не удалось загрузить reCAPTCHA. Попробуйте, пожалуйста, позже.';
            loadGrecaptcha()
                .then(() => {
                    /** @var {Object} grecaptcha */
                    window.grecaptcha.ready(() => {
                        window.grecaptcha.execute(getRecaptchaV3PublicKey(), { action })
                            .then((token) => {
                                resolve({ tokenV3: token, actionV3: action });
                            })
                            .catch((error) => reject(new Error(error?.message ?? errorMessage)))
                    })

                })
                .catch(() => reject(new Error('Не удалось загрузить reCAPTCHA. Попробуйте, пожалуйста, позже.')));
        });
    }

    onUploadProgress(event) {
        this.uploadProgress.value = event.loaded;

        const hasUploadedFile = this.uploadProgress.max === 0 || this.uploadProgress.value >= this.uploadProgress.max;
        const isLargeFile = this.uploadProgress.max >= FORM_LARGE_FILE_SIZE_THRESHOLD;

        if (this.isUploadingLargeFile && hasUploadedFile)
            this.isUploadingLargeFile = false;
        else if (!this.isUploadingLargeFile && !hasUploadedFile && isLargeFile)
            this.isUploadingLargeFile = true;
    }

    handleUploadError(event, reject) {
        reject({ error: { message: 'Не удалось загрузить. Попробуйте пожалуйста позже.' }});
    }

    handleResponse(event, resolve, reject) {
        // Get response
        let responseData = null;
        try {
            responseData = JSON.parse(this.sendFormRequest.response);
        } catch (error) {}

        if (this.sendFormRequest.status === 200 && responseData) {
            resolve(responseData);
        } else {
            reject(responseData ?? { error: { message: 'Ошибка, попробуйте позже' }});
        }
    }

    /**
     * @return {boolean}
     */
    get isDisabled() {
        return this.formElement.hasAttribute('disabled')
            && 'false' !== this.formElement.getAttribute('disabled');
    }

    /**
     * @param {boolean} v
     */
    set isDisabled(v) {
        if ('boolean' !== typeof v)
            return;

        if (v)
            this.formElement.setAttribute('disabled', 'disabled');
        else
            this.formElement.removeAttribute('disabled');

        for (const input of this.inputs) {
            input.isDisabled = v;
        }
    }

    /**
     * @return {boolean}
     */
    get isSending() {
        return this._isSending;
    }

    /**
     * @param {boolean} v
     */
    set isSending(v) {
        if ('boolean' !== typeof v)
            return;

        this._isSending = v;
        this.isDisabled = v;

        if (v) {
            this.formElement.classList.add(FORM_SENDING_CLASS);
        } else {
            this.isUploadingLargeFile = false;
            this.formElement.classList.remove(FORM_SENDING_CLASS);
        }
    }

    /**
     * @return {boolean}
     */
    get isUploadingLargeFile() {
        return this._isUploadingLargeFile;
    }

    /**
     * @param {boolean} v
     */
    set isUploadingLargeFile(v) {
        if ('boolean' !== typeof v)
            return;

        this._isUploadingLargeFile = v;

        if (v) {
            this.formElement.classList.add(FORM_LARGE_FILE_UPLOAD_CLASS);
        } else {
            this.formElement.classList.remove(FORM_LARGE_FILE_UPLOAD_CLASS);
        }
    }

    /**
     * @return {boolean}
     */
    get isSubmittedSuccessfully() {
        return this._isSubmittedSuccessfully;
    }

    /**
     * @param {boolean} v
     */
    set isSubmittedSuccessfully(v) {
        if ('boolean' !== typeof v)
            return;

        this._isSubmittedSuccessfully = v;

        if (v) {
            this.formElement.classList.add(FORM_SUBMITTED_SUCCESSFULLY_CLASS);
        } else {
            this.formElement.classList.remove(FORM_SUBMITTED_SUCCESSFULLY_CLASS);
        }

        const viewport = new Viewport();
        viewport.scrollIntoView(this.formElement, 40);
    }
}

/**
 * @return {PrkModernForm[]}
 */
export const initPrkModernForms = function() {
    const formsToInitialize = [...document.querySelectorAll(`form.${FORM_CLASS}:not(.${FORM_INITIALIZED_CLASS})`)];
    return formsToInitialize.map(form => new PrkModernForm(form));
}
